import { IcoSparkles16 } from '@onefootprint/icons';
import type { JSONContent } from '@tiptap/react';
import { cx } from 'class-variance-authority';
import { useRouter } from 'next/router';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatInput, type ChatInputHandle, ComposerDisclaimer, composerActions, type ResourceContext } from '@/components/agent-chat';
import ActionPills from './components/action-pills';
import BlockedComposer from './components/blocked-composer';
import type { TemplateBackedAgentId } from './data/agent-templates';
import { type AskPercyVariant, usePills } from './hooks/use-pills';
type AskPercyLabels = {
  title: string;
  subtitle: string;
  inputPlaceholder: string;
  loadingText: string;
  dropOverlay: string;
};
type AskPercySubmit = {
  query: string;
  files: File[];
  env?: string;
  restrictToSandbox: boolean;
  /** Route-derived resource the chat is about, when composing on a resource page with the chip active. */
  context?: ResourceContext;
  /** Resources the user @-mentioned in the composer; carried to the first message so it renders names. */
  mentions?: ResourceContext[];
};
type AskPercyProps = {
  labels: AskPercyLabels;
  isFirmEmployee?: boolean;
  /** Whether the tenant can flip between sandbox and live data. */
  canRestrictToSandbox?: boolean;
  /** Locks the env selector, for chats about a resource that exists in one env only. */
  pinnedEnv?: {
    value: 'sandbox' | 'production';
    tooltip: string;
  };
  /** Optional initial text to seed the prompt editor with — useful for tests/storybook. */
  defaultValue?: string;
  isLoading: boolean;
  canStartChat?: boolean;
  onSubmit: (params: AskPercySubmit) => void | Promise<void>;
  renderHeader?: React.ReactNode;
  variant?: AskPercyVariant;
  /** Centered on full pages; pinned to the bottom in sidebar and floating views. */
  placement?: 'centered' | 'bottom';
};
const AskPercy = ({
  labels,
  isFirmEmployee = false,
  canRestrictToSandbox = true,
  pinnedEnv,
  defaultValue,
  isLoading,
  canStartChat = true,
  onSubmit,
  renderHeader,
  variant,
  placement = 'centered'
}: AskPercyProps) => {
  const {
    t
  } = useTranslation('ask-percy');
  const {
    t: tForm
  } = useTranslation('ask-percy', {
    keyPrefix: 'form'
  });
  // Fullscreen vs floating/sidebar render the composer in different JSX subtrees, so switching
  // layout remounts ChatInput and would drop an unsent draft. AskPercy itself doesn't remount,
  // so hold the draft here and re-seed the fresh editor via `initialContent`.
  const draftRef = useRef<JSONContent | undefined>(undefined);
  const [previewPrompt, setPreviewPrompt] = useState<string | null>(null);
  const [seedText, setSeedText] = useState<string | undefined>();
  const [seedKey, setSeedKey] = useState(0);
  const [env, setEnv] = useState('');
  const [restrictToSandbox, setRestrictToSandbox] = useState(false);
  // A tenant that can't toggle is sandbox-restricted, so it can only create
  // sandbox chats — force sandbox regardless of the state above or `pinnedEnv`,
  // otherwise we'd submit is_live=true and the backend would reject it.
  const lockedEnv = canRestrictToSandbox ? pinnedEnv?.value : 'sandbox';
  const effectiveRestrictToSandbox = lockedEnv ? lockedEnv === 'sandbox' : restrictToSandbox;
  const sandboxToggleValue = effectiveRestrictToSandbox ? 'sandbox' : 'production';
  const disabled = isLoading || !canStartChat;
  // Distinct from `disabled`: the blocking overlay must not appear during the transient loading state.
  const blocked = !canStartChat;
  const router = useRouter();
  // Picking a suggestion fills the composer rather than sending it. The prompt only ever appears
  // in the placeholder, so a click that sends spends a run on wording nobody read. A `{|}` marker
  // still places the caret.
  const seedInput = (prompt: string) => {
    setSeedText(prompt);
    setSeedKey(k => k + 1);
  };
  const inputRef = useRef<ChatInputHandle>(null);

  // Backed templates reuse the /agents dropdown's bootstrap (deterministic, no Percy turn);
  // other pills fall through to seeding a Percy prompt in use-pills.
  const createFromTemplate = (template: TemplateBackedAgentId) => {
    if (!router.isReady) return;
    router.push(`/agents/builder?template=${template}`);
  };
  const pills = usePills(variant, seedInput, createFromTemplate);
  const isBottom = placement === 'bottom';
  const actions = composerActions({
    env: {
      value: sandboxToggleValue,
      onChange: lockedEnv ? undefined : next => setRestrictToSandbox(next === 'sandbox'),
      lockedTooltip: canRestrictToSandbox ? pinnedEnv?.tooltip : tForm($ => $.sandboxLocked)
    },
    percyEnv: isFirmEmployee ? {
      value: env,
      onChange: setEnv
    } : undefined
  });

  // Docked panel: a quiet heading beside the ghost-row suggestions. The centred landing keeps its
  // welcome block — full-page, a bare heading over a ghost list reads as an empty screen.
  const header = isBottom ? <h1 className="pl-2 text-heading-5 text-primary">{labels.title}</h1> : <div className="flex flex-col items-center gap-4 md:gap-6 max-w-[500px] text-center">
      <div className="flex items-center justify-center w-9 h-9 rounded-full bg-neutral-inverted shadow-[0_0_0_3px_var(--background-color-senary)]">
        <IcoSparkles16 color="quinary" />
      </div>
      <div className="flex flex-col items-center gap-2">
        <h1 className="text-heading-4 font-bold">{labels.title}</h1>
        <p className="text-body-2 text-tertiary">{labels.subtitle}</p>
      </div>
    </div>;
  const actionPills = pills && <div className={cx('w-full', !isBottom && 'flex justify-center', disabled && 'opacity-50')}>
      <ActionPills pills={pills} layout={isBottom ? 'list' : 'row'} onPreviewPromptChange={setPreviewPrompt} disabled={blocked} />
    </div>;
  const chatInput =
  // Bottom placement matches ChatPanel's input band (px-3, max-w-[820px]) so the composer
  // doesn't shift when the empty state swaps for the live chat. Centered page keeps its own.
  <div className={cx('mx-auto w-full', isBottom ? 'px-3 max-w-[820px]' : 'max-w-[680px]')}>
      <ChatInput ref={inputRef} placeholder={canStartChat ? previewPrompt || labels.inputPlaceholder : t($_0 => $_0.noPermission)} rows={isBottom ? 2 : 3} initialContent={draftRef.current ?? defaultValue} onChange={({
      content
    }) => {
      draftRef.current = content;
    }} seedText={seedText} seedKey={seedKey} disabled={disabled} isLive={!effectiveRestrictToSandbox} isLoading={isLoading} loadingText={labels.loadingText} onSendMessage={async (content_0, {
      files,
      context,
      mentions
    }) => await onSubmit({
      query: content_0,
      files: files ?? [],
      env: env.trim() || undefined,
      restrictToSandbox: effectiveRestrictToSandbox,
      context,
      mentions
    })} {...actions} />
    </div>;

  // BlockedComposer lays the pills out itself, outside the greeting's column — in the docked
  // panel the ghost rows need that column back or they hug the panel's left edge.
  const blockedPills = isBottom ? <div className="mx-auto w-full max-w-[820px] px-3">{actionPills}</div> : actionPills;
  const composerBlock = blocked ? <BlockedComposer pills={blockedPills} input={chatInput} message={t($_1 => $_1.noPermission)} /> : <div className={cx('relative w-full flex flex-col gap-4', !isBottom && 'items-center')}>
      {!isBottom && actionPills}
      {chatInput}
    </div>;
  if (isBottom) {
    // Greeting + pills centered, input pinned to the bottom. The disclaimer
    // flows below the input so a two-line wrap grows the stack instead of
    // overlapping it.
    return <div className="flex flex-col items-center pb-4 absolute inset-0">
        {renderHeader && <div className="absolute top-6 left-6">{renderHeader}</div>}
        {/* Suggestions sit with the greeting they answer rather than stacked on the composer, which
            keeps the composer a single object. Docked panel only — the centred landing page has room
            for both. */}
        <div className="flex-1 min-h-0 w-full flex flex-col justify-end px-3 pb-4">
          <div className="mx-auto w-full max-w-[820px] flex flex-col gap-3">
            {header}
            {!blocked && actionPills}
          </div>
        </div>
        <div className="w-full flex flex-col items-center">
          {composerBlock}
          <ComposerDisclaimer />
        </div>
      </div>;
  }
  return <div className="flex flex-col items-center justify-start md:justify-center px-4 md:px-8 absolute inset-0 overflow-y-auto pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] group-data-[keyboard-open]/app:pb-4 md:py-6">
      {renderHeader && <div className="absolute top-6 left-6">{renderHeader}</div>}
      <div className="flex flex-col items-center shrink-0 my-auto md:my-0 w-full gap-5 md:gap-8">
        {header}
        {composerBlock}
      </div>
      <ComposerDisclaimer className="shrink-0 mt-4 md:absolute md:bottom-4 md:left-4 md:right-4 md:mt-0" />
    </div>;
};
export type { AskPercyLabels, AskPercyProps, AskPercySubmit, AskPercyVariant };
export default AskPercy;